/**
 * Locale API Service — Default Locale APIs
 *
 * Handles communication with the tms-api-locale backend for:
 * - Fetching supported languages (GET /languages)          → PUBLIC
 * - Fetching the resolved locale bundle (GET /:domain)     → PUBLIC
 * - Fetching the full locale map (GET /map)                → JWT (SSR/debug fallback)
 *
 * These are the "default locale APIs" used during:
 *   • App initialization (language list + bundle loading)
 *   • Runtime locale switching
 *   • Post-auth bundle reload
 *
 * For override/admin APIs (keys, overrides, import, sync),
 * see locale-admin.service.ts.
 *
 * Backend: tms-api-locale (port 5011)
 * Gateway prefix: /locale → strips prefix → upstream /api/v1/locale/...
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type { Language, TranslationBundle } from '@tms-phoenix/tms-types';

// Re-export shared types so existing consumers keep working
export type { TranslationBundle, Language };

// ── API path ────────────────────────────────────────────────────────────────

/**
 * Full locale API prefix routed through the gateway.
 *
 * API_ROUTES.LOCALE = '/locale/api/v1'
 * Gateway strips '/locale' → upstream receives '/api/v1/locale/...'
 */
const LOCALE_API = `${API_ROUTES.LOCALE}/locale`;

// ── Default Locale APIs ─────────────────────────────────────────────────────

/**
 * GET /api/v1/locale/languages
 *
 * Fetch the list of active supported languages.
 * PUBLIC — no JWT required.
 *
 * Used by:
 *   • Locale store initialization (populate language picker)
 *   • Onboarding language selection step
 *   • Settings > Localization language dropdown
 *
 * Response is Redis-cached on the server for 24 hours.
 *
 * @returns Array of Language objects (language_code, language_name, native_name, text_direction, is_default)
 */
export async function fetchAvailableLanguages(): Promise<Language[]> {
  const api = getApiClient();
  const response = await api.get<Language[]>(
    `${LOCALE_API}/languages`,
  );
  return response.data;
}

/**
 * GET /api/v1/locale/:domain
 *
 * Fetch the full resolved locale bundle for a domain.
 * PUBLIC — no JWT required.
 *
 * The backend resolves the tenant **and its default language** from the
 * domain (hostname) and returns a **raw flat JSON object** (no API envelope).
 * The resolved bundle applies the 3-level resolution chain:
 *   custom_value → translated_value → base_value
 *
 * NOTE: This endpoint does not accept a language parameter — the language
 * is determined server-side from the tenant's configured default.
 * Client-side language switching is not supported.
 *
 * Used by:
 *   • i18next HTTP backend (loadPath) — primary bundle loading
 *   • Post-auth bundle reload
 *
 * @param domain - Full hostname (e.g. tenant1dev.staging.timelesslabs.com)
 * @returns Flat `{ key: resolvedValue }` JSON bundle in the tenant's default language
 */
export async function fetchLocaleBundleByDomain(
  domain: string,
): Promise<TranslationBundle> {
  const api = getApiClient();
  const response = await api.get<TranslationBundle>(
    `${LOCALE_API}/${encodeURIComponent(domain)}`,
  );
  return response.data;
}

/**
 * GET /api/v1/locale/map
 *
 * SSR / debug fallback for the full resolved locale bundle.
 * JWT required — tenant_id is extracted from the token payload.
 *
 * In production, browsers fetch the bundle from CloudFront via the
 * /:domain endpoint above. Use /map for:
 *   • Server-side rendering
 *   • Local development without a CDN
 *   • Debugging / verifying resolved values
 *
 * Used by:
 *   • Post-auth bundle reload when tenantId is not available in config
 *   • Fallback when the CDN-origin endpoint fails
 *
 * @returns Flat `{ key: resolvedValue }` map inside the standard API envelope
 */
export async function fetchLocaleMap(): Promise<TranslationBundle> {
  const api = getApiClient();
  const response = await api.get<TranslationBundle>(
    `${LOCALE_API}/map`,
  );
  return response.data;
}
